/**
 * The arena sidecar the asset pipeline writes next to every arena GLB (`<arena>.colliders.json`).
 * Client and server build their Rapier worlds from it, so neither ever needs the render mesh for
 * physics, and since Phase 8 it also carries the arena's playable features: hazards, pickups,
 * boost pads, moving platforms and low-visibility zones, all stepped by the shared simulation so
 * both sides agree on them. Units are metres, Y is up, forward at yaw 0 is -Z.
 */
import { z } from 'zod';
const finite = z.number().finite();
const vec3 = z.tuple([finite, finite, finite]);
const positiveVec3 = z.tuple([finite.positive(), finite.positive(), finite.positive()]);
const id = z.string().min(1).max(40);
export const BoxColliderSchema = z
    .object({
    type: z.literal('box'),
    name: z.string().optional(),
    center: vec3,
    halfExtents: positiveVec3,
    /** Rotation about +Y in radians; ignored when `rotation` is present. */
    yaw: finite.default(0),
    /** Unit quaternion [x, y, z, w]; needed for ramps and other pitched boxes. */
    rotation: z.tuple([finite, finite, finite, finite]).optional(),
})
    .strict();
/** A yaw-rotated box: the volume a feature occupies. */
export const YawBoxSchema = z
    .object({ center: vec3, halfExtents: positiveVec3, yaw: finite.default(0) })
    .strict();
/** A volume that hurts, heats or kills a mech standing in it. */
export const HazardZoneSchema = z
    .object({
    id,
    box: YawBoxSchema,
    damagePerSecond: finite.min(0).max(200).default(0),
    heatPerSecond: finite.min(0).max(100).default(0),
    instantKill: z.boolean().default(false),
})
    .strict()
    .refine((h) => h.instantKill || h.damagePerSecond > 0 || h.heatPerSecond > 0, {
    message: 'a hazard must hurt, heat or kill',
});
export const PICKUP_KINDS = ['shieldCell', 'heatSink'];
/** A shield cell restores `amount` shield; a heat sink removes `amount` heat. */
export const PickupSchema = z
    .object({
    id,
    kind: z.enum(PICKUP_KINDS),
    position: vec3,
    radius: finite.min(0.5).max(6).default(1.5),
    amount: finite.positive().max(2000),
    respawnSeconds: finite.min(5).max(120).default(20),
})
    .strict();
/** Stepping into the box launches the mech along the box's yaw (forward) and upward. */
export const BoostPadSchema = z
    .object({
    id,
    box: YawBoxSchema,
    launch: z.object({ forward: finite.min(0).max(40), up: finite.min(0).max(40) }).strict(),
    cooldownSeconds: finite.min(0).max(10).default(1),
})
    .strict();
const LinearPathSchema = z
    .object({
    kind: z.literal('linear'),
    from: vec3,
    to: vec3,
    periodSeconds: finite.min(4).max(60),
    /** Fraction of a period the motion starts into (0..1). */
    phase: finite.min(0).max(1).default(0),
    /** Fraction of each leg spent resting at the end point (0..0.4). */
    dwell: finite.min(0).max(0.4).default(0.1),
})
    .strict();
const CircularPathSchema = z
    .object({
    kind: z.literal('circular'),
    center: vec3,
    radius: finite.positive().max(40),
    periodSeconds: finite.min(4).max(60),
    phase: finite.min(0).max(1).default(0),
    clockwise: z.boolean().default(true),
})
    .strict();
export const PlatformPathSchema = z.discriminatedUnion('kind', [
    LinearPathSchema,
    CircularPathSchema,
]);
/** A box that moves on a fixed schedule; its pose is a pure function of the tick. */
export const PlatformSchema = z
    .object({ id, halfExtents: positiveVec3, yaw: finite.default(0), path: PlatformPathSchema })
    .strict();
/** Inside the box a mech is hidden from the radar, the nameplate and the bot beyond `revealDistance`. */
export const ConcealmentZoneSchema = z
    .object({ id, box: YawBoxSchema, revealDistance: finite.min(0).max(60).default(10) })
    .strict();
export const ArenaEnvironmentSchema = z
    .object({
    /** Processed-asset path of the sky (image-based lighting and backdrop). */
    hdri: z.string().min(1),
    environmentIntensity: finite.positive().max(4).default(0.5),
    backgroundIntensity: finite.positive().max(4).default(0.55),
})
    .strict();
/** The sky every arena used before sidecars named their own (version 1 files and fixtures). */
export const DEFAULT_ARENA_ENVIRONMENT = Object.freeze({
    hdri: 'hdri/industrial_pipe_and_valve_01_2k.hdr',
    environmentIntensity: 0.5,
    backgroundIntensity: 0.55,
});
function uniqueIds(list, what, ctx, path) {
    const seen = new Set();
    for (const item of list) {
        if (seen.has(item.id)) {
            ctx.addIssue({ code: 'custom', message: `duplicate ${what} id "${item.id}"`, path: [path] });
        }
        seen.add(item.id);
    }
}
export const ArenaCollidersSchema = z
    .object({
    /** 1: colliders and spawns only; 2: adds the environment and the playable features. */
    version: z.union([z.literal(1), z.literal(2)]),
    arena: z.string().min(1),
    /** Display name; the id when absent. */
    name: z.string().min(1).max(40).optional(),
    units: z.literal('m'),
    up: z.literal('y'),
    bounds: z.object({ min: vec3, max: vec3 }).strict(),
    /** Mechs below this height are dead (kill volume). */
    killY: finite,
    spawns: z.array(z.object({ id, position: vec3, yaw: finite }).strict()).min(2),
    colliders: z.array(BoxColliderSchema).min(1),
    environment: ArenaEnvironmentSchema.default({ ...DEFAULT_ARENA_ENVIRONMENT }),
    hazards: z.array(HazardZoneSchema).default([]),
    pickups: z.array(PickupSchema).default([]),
    boostPads: z.array(BoostPadSchema).default([]),
    platforms: z.array(PlatformSchema).default([]),
    concealment: z.array(ConcealmentZoneSchema).default([]),
})
    .strict()
    .superRefine((def, ctx) => {
    uniqueIds(def.spawns, 'spawn', ctx, 'spawns');
    uniqueIds(def.hazards, 'hazard', ctx, 'hazards');
    uniqueIds(def.pickups, 'pickup', ctx, 'pickups');
    uniqueIds(def.boostPads, 'boost pad', ctx, 'boostPads');
    uniqueIds(def.platforms, 'platform', ctx, 'platforms');
    uniqueIds(def.concealment, 'concealment zone', ctx, 'concealment');
});
export function parseArenaColliders(json) {
    return ArenaCollidersSchema.parse(json);
}
/** The arena's display name (the id when the sidecar names none). */
export function arenaDisplayName(def) {
    return def.name ?? def.arena;
}
//# sourceMappingURL=schema.js.map